/**
 * # Reusable carousel — Alpine data factory
 *
 * A scroll-snap carousel that works for every carousel shape in the project.
 * The *visual* layout (how many items per slide, whether neighbours peek beyond
 * the bleed, gaps) is expressed entirely in CSS/utility classes on the markup —
 * this component only owns **behaviour**: which slide is currently centered,
 * arrow navigation, and the per-slide `is-current` flag.
 *
 * ## Why scroll-snap (and not transforms)
 * The track is a native horizontally-scrollable, `scroll-snap-type: x` flex row.
 * That gives us touch/trackpad swiping, momentum, keyboard focus scrolling and
 * the "centered item with neighbours peeking beyond the full-bleed edge" layout
 * (`scroll-snap-align: center` + side padding) for free — no transform maths,
 * no width measuring, naturally responsive. JS only *reads* the scroll position
 * to know the active slide and *writes* it (smooth `scrollTo`) for the arrows.
 *
 * ## Markup contract
 * - Root element: `x-data="carousel"` (or a component that spreads this factory,
 *   e.g. `gallery`).
 * - The scroll track: `x-ref="track"`, containing the slides.
 * - Each slide: `data-slide` attribute.
 *
 * ## Exposed state / API (for `:class`, `:disabled`, `@click`)
 * - `current`  — index of the centered slide (kept in sync with manual scroll)
 * - `count`    — number of slides
 * - `atStart` / `atEnd` — booleans for disabling the prev/next arrows
 * - `prev()` / `next()` / `goTo(i)` — smooth-scroll a slide to centre
 * - `isActive(i)` — `current === i`
 *
 * The active slide also receives an `is-current` class (toggled in JS) so CSS
 * can reveal the caption of the centered item only — see `image_gallery_item`.
 */

export interface CarouselComponent {
  current: number;
  count: number;
  atStart: boolean;
  atEnd: boolean;
  _track: HTMLElement | null;
  _slides: HTMLElement[];
  _onScroll: () => void;
  _raf: number;
  init(): void;
  destroy(): void;
  initCarousel(): void;
  destroyCarousel(): void;
  _syncActive(): void;
  goTo(index: number): void;
  next(): void;
  prev(): void;
  isActive(index: number): boolean;
}

export function carousel(): CarouselComponent {
  return {
    current: 0,
    count: 0,
    atStart: true,
    atEnd: false,

    _track: null,
    _slides: [],
    _onScroll: () => {},
    _raf: 0,

    // Alpine lifecycle for standalone `x-data="carousel"` use (e.g.
    // `item_recommended`). Components that compose the carousel by spreading
    // `...carousel()` (e.g. `gallery`) define their own `init()`/`destroy()`
    // *after* the spread — those override these and call `initCarousel()` /
    // `destroyCarousel()` themselves alongside their extra layer.
    init(): void {
      this.initCarousel();
    },

    destroy(): void {
      this.destroyCarousel();
    },

    initCarousel(): void {
      const track = (this as unknown as { $refs: Record<string, HTMLElement> }).$refs.track;
      if (!track) {
        return;
      }
      this._track = track;
      this._slides = Array.from(track.querySelectorAll<HTMLElement>('[data-slide]'));
      this.count = this._slides.length;
      // Recompute the active slide on every scroll, throttled to one rAF.
      this._onScroll = (): void => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this._syncActive());
      };
      track.addEventListener('scroll', this._onScroll, { passive: true });
      this._syncActive();
    },

    destroyCarousel(): void {
      this._track?.removeEventListener('scroll', this._onScroll);
      cancelAnimationFrame(this._raf);
    },

    /** Pick the slide whose centre is nearest the track centre as the active one. */
    _syncActive(): void {
      const track = this._track;
      if (!track || !this._slides.length) {
        return;
      }
      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      this._slides.forEach((slide, i) => {
        const r = slide.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      this.current = best;
      // Arrow-disable state comes from the real scroll position, not the
      // nearest-to-centre index: in a left-flush grid the centremost slide at
      // scrollLeft 0 is the middle column (not slide 0), so an index-based
      // `atStart` would wrongly enable "prev" at rest. Scroll-based edges are
      // also correct for the centered-peek layout and disable both arrows when
      // the content doesn't overflow.
      //
      // EDGE absorbs subpixel rounding: a flush N-up grid whose item count
      // equals the column count is mathematically exactly 100% wide, but the
      // browser rounds each `basis-[calc()]` column independently, leaving
      // `scrollWidth` a few px past `clientWidth`. Without this tolerance the
      // "next" arrow stays enabled and a click nudges those phantom px (e.g. 3
      // items in a 3-up grid). A genuine extra slide adds at least a full slide
      // width — far beyond EDGE — so real carousels are unaffected, and because
      // this reads live geometry it stays correct across breakpoints (3 items
      // at 1-up/2-up still overflow by a whole column → arrows stay enabled).
      const EDGE = 24;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const overflows = maxScroll > EDGE;
      this.atStart = !overflows || track.scrollLeft <= 1;
      this.atEnd = !overflows || track.scrollLeft >= maxScroll - 1;
      this._slides.forEach((slide, i) => slide.classList.toggle('is-current', i === best));
    },

    /** Smooth-scroll the track so slide `index` is centred — never scrolls the page. */
    goTo(index: number): void {
      const i = Math.max(0, Math.min(index, this.count - 1));
      const track = this._track;
      const slide = this._slides[i];
      if (!track || !slide) {
        return;
      }
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left - trackRect.left + track.scrollLeft + slideRect.width / 2;
      track.scrollTo({ left: slideCenter - track.clientWidth / 2, behavior: 'smooth' });
    },

    next(): void {
      this.goTo(this.current + 1);
    },

    prev(): void {
      this.goTo(this.current - 1);
    },

    isActive(index: number): boolean {
      return this.current === index;
    },
  };
}
